import React, { FC, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper";
import { Mousewheel } from "swiper/modules";
import "swiper/css";
import "swiper/css/mousewheel";
import {
  Benefits_benefitItemsCollection_items,
} from "../../../../__generated__/graphql";
import classnames from "classnames";
import { CarouselControls } from "ui-kit";
import TextFieldsV2 from "../TextFieldsV2";
import FeatureCard from "./FeatureCard";
import styles from "./BenefitsCarousel.module.css";

type BenefitsCarouselProps = {
  title?: string | null;
  text?: string | null;
  items: Benefits_benefitItemsCollection_items[];
};

const BenefitsCarousel: FC<BenefitsCarouselProps> = ({
  title,
  text,
  items,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const swiperRef = useRef<SwiperType | null>(null);

  const syncEdges = (swiper: SwiperType) => {
    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
  };

  return (
    <section className={styles.section}>
      <div className={styles.header}>
        <TextFieldsV2
          title={title}
          text={text}
          center
          titleGradient
          gutterBottom={5}
        />
      </div>
      <div className={styles.carouselWrapper}>
        <Swiper
          slidesPerView="auto"
          spaceBetween={16}
          centeredSlides
          modules={[Mousewheel]}
          mousewheel={{ forceToAxis: true }}
          breakpoints={{
            500: { centeredSlides: false },
          }}
          className={styles.carousel}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
            syncEdges(swiper);
          }}
          onSlideChange={(swiper) => {
            setActiveIndex(swiper.activeIndex);
            syncEdges(swiper);
          }}
        >
          {items.map((item) => (
            <SwiperSlide key={item.title} className={styles.slide}>
              <FeatureCard
                title={item.title}
                description={item.description ?? null}
                text={item.text}
                color={item.color ?? null}
                image={item.image ?? null}
                variant="carousel"
              />
            </SwiperSlide>
          ))}
        </Swiper>
        <CarouselControls
          count={items.length}
          activeIndex={activeIndex}
          onDotClick={(index) => swiperRef.current?.slideTo(index)}
          onPrev={() => swiperRef.current?.slidePrev()}
          onNext={() => swiperRef.current?.slideNext()}
          prevDisabled={isBeginning}
          nextDisabled={isEnd}
          className={classnames(styles.controls, {
            [styles.controlsDesktopHidden]: items.length <= 4,
          })}
        />
      </div>
    </section>
  );
};

export default BenefitsCarousel;
